// Founder-facing "should I do something about this" panels — trial and
// free→Pro conversion, solo-Pro engagement, and a churn-risk (going-quiet)
// org list. Distinct from Overview, which is activity/health rollups.

import { el, fmtInt, fmtPct, fmtCompact, relativeTime } from '../ui/dom';
import { renderShell, renderError } from '../ui/shell';
import { kpiCard, kpiGrid, panel, panelGrid, segmented, caption } from '../ui/components';
import { funnelChart, barList, type BarRow } from '../ui/charts';
import {
  getConversion,
  getSoloUsage,
  type ConversionStats,
  type SoloUsage,
  type DataBasis,
} from '../api/platform';
import { listOrganizations } from '../api/monitoring';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import type { OrganizationSummary } from '../api/types';

function loadingBox(): HTMLElement {
  return el('div', { class: 'loading' }, ['Loading…']);
}

async function settle<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    return null;
  }
}

function basisLabel(basis: DataBasis | string): string {
  switch (basis) {
    case 'ok':
      return 'basis: ok';
    case 'estimated':
      return 'basis: estimated';
    case 'partial':
      return 'basis: partial data';
    case 'unavailable':
      return 'basis: unavailable';
    default:
      return `basis: ${basis}`;
  }
}

function emptyNote(text: string): HTMLElement {
  return el('p', { class: 'panel-empty' }, [text]);
}

export async function renderDecisions({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'decisions');

  let windowDays = 30;
  const cap = caption('Conversion, engagement, and churn-risk signals — the questions Overview doesn’t answer.');
  const rangeHost = el('div', { class: 'seg-host' });
  const head = el('div', { class: 'view-head' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['Decisions'])]),
    rangeHost,
  ]);
  const body = el('div', {}, [loadingBox()]);
  content.append(head, cap, body);

  function renderRange(): void {
    rangeHost.replaceChildren(
      segmented(
        [
          { label: '7 days', value: '7' },
          { label: '30 days', value: '30' },
          { label: '90 days', value: '90' },
        ],
        String(windowDays),
        (v) => {
          windowDays = Number(v);
          renderRange();
          void load();
        },
      ),
    );
  }

  async function load(): Promise<void> {
    body.replaceChildren(loadingBox());
    try {
      const [conversion, solo, quiet] = await Promise.all([
        settle(getConversion(windowDays)),
        settle(getSoloUsage()),
        settle(listOrganizations({ sortBy: 'lastScanAt', order: 'asc', pageSize: 10 })),
      ]);
      body.replaceChildren(
        panelGrid([
          trialConversionPanel(conversion, windowDays),
          proFunnelPanel(conversion, windowDays),
          soloEngagementPanel(solo),
        ]),
        goingQuietSection(quiet?.items ?? null),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load.');
    }
  }

  renderRange();
  await load();
}

function trialConversionPanel(
  conversion: ConversionStats | null,
  windowDays: number,
): HTMLElement {
  if (!conversion) {
    return panel('Trial → paid conversion', [emptyNote('Failed to load.')], {
      subtitle: `Last ${windowDays} days`,
    });
  }
  const t = conversion.trials;
  const steps = [
    { label: 'Started', value: t.started },
    { label: 'Converted', value: t.converted },
  ];
  return panel(
    'Trial → paid conversion',
    [
      t.started > 0 ? funnelChart(steps) : emptyNote('No trials started in this window.'),
      el('div', { class: 'panel-note' }, [
        `${fmtInt(t.expired)} expired without converting · ${
          t.rate === null ? 'rate —' : `rate ${fmtPct(t.rate)}`
        } · ${basisLabel(t.basis)}`,
      ]),
    ],
    { subtitle: `Last ${windowDays} days` },
  );
}

function proFunnelPanel(
  conversion: ConversionStats | null,
  windowDays: number,
): HTMLElement {
  if (!conversion) {
    return panel('Free → Pro funnel', [emptyNote('Failed to load.')], {
      subtitle: `Last ${windowDays} days`,
    });
  }
  const f = conversion.proFunnel;
  const steps = [
    { label: 'Paywall views', value: f.paywallViews },
    { label: 'Purchases', value: f.purchases },
  ];
  return panel(
    'Free → Pro funnel',
    [
      f.paywallViews > 0
        ? funnelChart(steps)
        : emptyNote('No paywall views recorded in this window.'),
      el('div', { class: 'panel-note' }, [
        f.rate === null ? 'Conversion rate —' : `Conversion rate ${fmtPct(f.rate)}`,
      ]),
    ],
    { subtitle: `Last ${windowDays} days` },
  );
}

function soloEngagementPanel(solo: SoloUsage | null): HTMLElement {
  if (!solo) {
    return panel('Solo-Pro engagement', [emptyNote('Failed to load.')], {
      subtitle: 'All-time · anonymous',
    });
  }
  const rows: BarRow[] = solo.topUsers.map((u, i) => ({
    label: `#${i + 1} · last active ${relativeTime(u.lastActiveDate)}`,
    value: u.scanCount,
  }));
  return panel(
    'Solo-Pro engagement',
    [
      kpiGrid([
        kpiCard('Solo users', fmtInt(solo.totalUsers)),
        kpiCard('Active last 30d', fmtInt(solo.activeLast30d), {
          tone: solo.activeLast30d > 0 ? 'ok' : 'default',
        }),
        kpiCard('Lifetime scans', fmtCompact(solo.lifetimeScans)),
      ]),
      rows.length
        ? el('div', { class: 'panel-subhead' }, ['Top users by scan count (anonymous)'])
        : null,
      rows.length ? barList(rows) : emptyNote('No solo scan activity yet.'),
    ],
    { subtitle: 'All-time · anonymous, no emails or ids' },
  );
}

function goingQuietSection(items: OrganizationSummary[] | null): HTMLElement {
  const rows = items ?? [];
  return panel(
    'Going quiet',
    [
      rows.length
        ? el('div', { class: 'table-wrap' }, [
            el('table', { class: 'data-table' }, [
              el('thead', {}, [
                el('tr', {}, [
                  el('th', { textContent: 'Team' }),
                  el('th', { textContent: 'Tier' }),
                  el('th', { textContent: 'Status' }),
                  el('th', { textContent: 'Last scan' }),
                ]),
              ]),
              el(
                'tbody',
                {},
                rows.map((o) =>
                  el(
                    'tr',
                    {
                      class: 'row-clickable',
                      onClick: () => navigate(`/organizations/${o.id}`),
                    },
                    [
                      el('td', {}, [
                        el('div', { class: 'cell-strong', textContent: o.name }),
                        el('div', { class: 'cell-muted', textContent: o.slug }),
                      ]),
                      el('td', {}, [
                        el('span', { class: 'pill', textContent: o.subscriptionTier }),
                      ]),
                      el('td', {}, [
                        el('span', {
                          class: o.suspendedAt
                            ? 'pill pill-danger'
                            : o.subscriptionStatus === 'ACTIVE'
                              ? 'pill pill-ok'
                              : 'pill pill-warn',
                          textContent: o.suspendedAt ? 'SUSPENDED' : o.subscriptionStatus,
                        }),
                      ]),
                      el('td', { class: 'cell-muted' }, [
                        relativeTime(o.metrics.lastScanAt),
                      ]),
                    ],
                  ),
                ),
              ),
            ]),
          ])
        : emptyNote('No teams to show.'),
    ],
    { subtitle: 'Teams sorted by oldest last-scan — earliest churn-risk signal', wide: true },
  );
}
